import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { PermissionService } from '../services/permission.service';
import { UserContextService } from '../services/user-context/user-context.service';

/**
 * Creates a route guard that controls access based on user permissions.
 * Redirects to /home if the user lacks the required permissions.
 *
 * @param permissions - Permission string(s) to check against
 * @param mode - 'one' requires at least one permission, 'all' requires every permission. Defaults to 'one'.
 * @param options.allowArcUsers - If true, acting ARC users bypass the permission check. Defaults to false.
 *
 * @example
 * ```typescript
 * const routes: Routes = [
 *   {
 *     path: 'marketing',
 *     canActivate: [permissionGuard(MarketingPermissions)],
 *     loadChildren: () => import('./marketing.routes')
 *   },
 *   {
 *     path: 'settings',
 *     canActivate: [permissionGuard(MarketingPermissions, 'one', { allowArcUsers: true })],
 *     loadComponent: () => import('./settings.component')
 *   }
 * ];
 * ```
 */
export const permissionGuard = (
  permissions: string | string[],
  mode: 'one' | 'all' = 'one',
  options?: { allowArcUsers?: boolean }
): CanActivateFn => {
  return () => {
    const permissionService = inject(PermissionService);
    const router = inject(Router);

    if (options?.allowArcUsers && inject(UserContextService).IsActingArcUser()) {
      return true;
    }

    const hasAccess = mode === 'all'
      ? permissionService.hasAllPermissions(permissions)
      : permissionService.hasOnePermission(permissions);

    return hasAccess || router.createUrlTree(['/home']);
  };
};
